import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

const categoryIcons: Record<string, string> = {
  'Schuhe': 'ri-footprint-line',
  'Hoodies/Sweater': 'ri-t-shirt-line',
  'T-Shirts': 'ri-shirt-line',
  'Jacken': 'ri-t-shirt-2-line',
  'Hosen/Shorts': 'ri-checkbox-blank-line',
  'Kopfbedeckung': 'ri-user-5-line',
  'Sets': 'ri-stack-line',
  'Unterwäsche': 'ri-contrast-drop-line',
  'Trikots': 'ri-football-line',
  'Accessoires': 'ri-glasses-line',
  'Taschen': 'ri-handbag-line',
  'Gürtel': 'ri-link-m',
  'Parfums': 'ri-drop-line',
  'Sonstiges': 'ri-more-line',
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1440px] mx-auto px-4 md:px-10 h-16 flex items-center justify-between">
        <Link
          to="/"
          className="text-lg md:text-xl font-heading font-bold text-foreground-950 whitespace-nowrap tracking-tight"
        >
          OOPBUY Spreadsheet
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
          >
            Startseite
          </Link>
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>Kategorien</span>
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-base"></i>
              </span>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-[420px] overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className={categoryIcons[cat.name] || 'ri-price-tag-3-line'}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/artikel/oopbuy-spreadsheet-guide"
            className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
          >
            Einkaufsführer
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            OOPBUY Spreadsheet
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </span>
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-foreground-950 text-white text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </span>
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-4 flex flex-col gap-2 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl transition-colors"
          >
            <span className="w-6 h-6 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className="ri-home-4-line text-sm"></i>
            </span>
            Startseite
          </Link>

          <Link
            to="/artikel/oopbuy-spreadsheet-guide"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl transition-colors"
          >
            <span className="w-6 h-6 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
              <i className="ri-book-open-line text-sm"></i>
            </span>
            Einkaufsführer
          </Link>

          <div className="border-t border-background-200/60 pt-2 mt-1">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-500 uppercase tracking-wider"
            >
              <span>Produkt Kategorien</span>
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line"></i>
              </span>
            </button>
            {mobileCatOpen && (
              <div className="grid grid-cols-2 gap-1.5 px-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-xl transition-colors"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400 flex-shrink-0">
                      <i className={categoryIcons[cat.name] || 'ri-price-tag-3-line'}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-background-200/60 pt-3 mt-2 flex flex-col gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              OOPBUY Spreadsheet
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-foreground-950 text-white text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap"
            >
              Buy It Now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}